import { Box, Typography } from '@mui/material';
import type { MonitorPalette } from '../chart/palette';
import type { Summary, SummaryStat } from './summary';

const NBSP = ' ';

const VALUE_H = { xs: 36, md: 46 };

const COLUMNS = {
    xs: 'repeat(2, minmax(0, 1fr))',
    md: '200px 140px 140px 240px 200px',
};

const COLUMNS_PENDING = {
    xs: 'repeat(2, minmax(0, 1fr))',
    md: '200px 140px 140px 140px 240px 200px',
};

const captionSx = {
    color: 'text.secondary',
    display: 'block',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '11px',
};

interface CellProps extends SummaryStat {
    size?: 'lg' | 'md' | 'sm';
    ink: string;
}

function Cell({ label, value, sub, color, size = 'md', ink }: CellProps) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography component="div" sx={captionSx}>
                {label}
            </Typography>
            <Box
                sx={{
                    height: VALUE_H,
                    display: 'flex',
                    alignItems: 'flex-end',
                }}
            >
                <Typography
                    component="div"
                    sx={{
                        color: color ?? ink,
                        fontSize:
                            size === 'lg'
                                ? { xs: '2.1rem', md: '2.6rem' }
                                : size === 'md'
                                  ? { xs: '1.25rem', md: '1.45rem' }
                                  : '1.05rem',
                        fontWeight: size === 'sm' ? 400 : 500,
                        lineHeight: 1,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {value}
                </Typography>
            </Box>
            <Typography component="div" sx={captionSx}>
                {sub ?? NBSP}
            </Typography>
        </Box>
    );
}

function DistributionBar({
    summary,
    palette,
}: {
    summary: Summary;
    palette: MonitorPalette;
}) {
    const outline = `inset 0 0 0 1px ${palette.modalOutline}`;
    return (
        <Box sx={{ mt: 2 }}>
            <Typography component="div" sx={{ ...captionSx, mb: 0.75 }}>
                {summary.binsLabel}
                {'  ·  '}
                {summary.binsNote}
            </Typography>
            <Box sx={{ display: 'flex', gap: '2px', height: 10, mb: 1 }}>
                {summary.bins.map(b => (
                    <Box
                        key={b.label}
                        sx={{
                            flex: b.share,
                            minWidth: b.share > 0 ? '2px' : 0,
                            backgroundColor: b.color,
                            opacity: b.isModal ? 1 : 0.65,
                            borderRadius: '2px',
                            boxShadow: b.isModal ? outline : 'none',
                            transition: 'flex-grow 120ms linear',
                        }}
                    />
                ))}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: 1.5, md: 2.5 },
                }}
            >
                {summary.bins.map(b => (
                    <Box
                        key={b.label}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                        }}
                    >
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '2px',
                                backgroundColor: b.color,
                                opacity: b.isModal ? 1 : 0.65,
                                boxShadow: b.isModal ? outline : 'none',
                            }}
                        />
                        <Typography
                            component="span"
                            sx={{
                                fontSize: '11px',
                                color: b.isModal
                                    ? 'text.primary'
                                    : 'text.secondary',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {b.label} {b.pctLabel}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

interface PiprSummaryProps {
    summary: Summary;
    pendingOpen: boolean;
    palette: MonitorPalette;
}

export function PiprSummary({
    summary,
    pendingOpen,
    palette,
}: PiprSummaryProps) {
    const ink = palette.ink;
    return (
        <Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: pendingOpen
                        ? COLUMNS_PENDING
                        : COLUMNS,
                    justifyContent: 'start',
                    columnGap: { xs: 2, md: 3 },
                    rowGap: 2,
                }}
            >
                <Cell
                    label="PIPR"
                    value={summary.headline}
                    sub={summary.headlineCaption}
                    size="lg"
                    ink={ink}
                />
                <Cell {...summary.anchor} ink={ink} />
                {pendingOpen &&
                    (summary.decided ? (
                        <Cell {...summary.decided} ink={ink} />
                    ) : (
                        <Cell label={NBSP} value={NBSP} ink={ink} />
                    ))}
                <Cell {...summary.priced} ink={ink} />
                <Cell {...summary.decision} size="md" ink={ink} />
                <Cell {...summary.meeting} ink={ink} />
            </Box>
            <DistributionBar summary={summary} palette={palette} />
            <Typography
                component="div"
                sx={{
                    ...captionSx,
                    mt: 1.25,
                    minHeight: 18,
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {summary.note || NBSP}
            </Typography>
        </Box>
    );
}
